/**
 * DataHubTabContainer - Logic for the Data Hub tab
 *
 * Creates a stable render prop callback for PerformanceAnalytics so the
 * comparison player can flow down to IndividualMetricInsights without
 * allocating a new function reference on every render.
 * @remarks This is a Container component - handles business logic and state
 */

import { useCallback } from "react";

import DataHubTabView from "./DataHubTabView";
import IndividualMetricInsights from "@/features/player-detail/components/organisms/IndividualMetricInsights";

import type {
  PerformanceAnalyticsPlayerScoresFragment,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";

/**
 * Props for DataHubTabContainer component
 */
interface DataHubTabContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen: boolean;
}

/**
 * DataHubTabContainer manages the render prop for IndividualMetricInsights
 * @param props - Container props
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.isSmallScreen - Whether the screen is mobile sized
 */
const DataHubTabContainer = ({
  isDark,
  player,
  isSmallScreen,
}: DataHubTabContainerProps) => {
  const renderAdditionalContent = useCallback(
    (comparisonPlayer: PerformanceAnalyticsPlayerScoresFragment | null) =>
      player ? (
        <IndividualMetricInsights
          player={player}
          comparisonPlayer={comparisonPlayer}
        />
      ) : undefined,
    [player]
  );

  return (
    <DataHubTabView
      isDark={isDark}
      player={player}
      isSmallScreen={isSmallScreen}
      renderAdditionalContent={renderAdditionalContent}
    />
  );
};

export default DataHubTabContainer;
